import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, effect, ElementRef, EventEmitter, input, Output, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { VideoState } from '../../models/video-state';

@Component({
  selector: 'app-video-player',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    DatePickerModule
  ],
  templateUrl: './video-player.component.html',
  styles: ``,
})
export class VideoPlayerComponent implements AfterViewInit {

  initialTime = input.required<Date>();
  timerTick = input.required<VideoState>();
  @Output() metadataLoaded = new EventEmitter<{ durationMs: number }>();
  @Output() messageRequest = new EventEmitter<string>();
  @Output() videoStartSelected = new EventEmitter<number>();

  videoUrl: string | null = null;
  videoReady = signal(false);

  @ViewChild('videoRef') videoRef!: ElementRef<HTMLVideoElement>;

  showDialog: boolean = false;
  startTime: Date | undefined;

  // Metadata
  videoDurationMs = 0;
  videoStartMs = 0;

  constructor() {
    // Sincronizzazione dal parent → video
    effect(() => {
      if (!this.videoReady()) return;

      const state = this.timerTick();

      if (this.videoRef !== undefined) {
        const video = this.videoRef.nativeElement;

        this.syncVideo(state, video);
      }
    });
  }

  ngAfterViewInit() {
    this.videoReady.set(true);
  }

  formatDuration(ms: number): string {
    if (ms <= 0) return "--";

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }

    return `${minutes}m ${seconds}s`;
  }

  formatTime(ms: number): string {
    if (!this.videoUrl || ms <= 0) return "--";

    const date = new Date(ms);
    const hh = date.getHours().toString().padStart(2, '0');
    const mm = date.getMinutes().toString().padStart(2, '0');
    const ss = date.getSeconds().toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }

  onCloseDialog() {
    this.videoUrl = null;
    this.showDialog = false;

    this.messageRequest.emit("Ora di inizio non impostata. Il video NON sarà riprodotto!");
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    this.videoUrl = URL.createObjectURL(file);

    this.startTime = this.initialTime();
    this.showDialog = true;
  }

  onMetadataLoaded() {
    if (this.videoRef !== undefined) {
      const video = this.videoRef.nativeElement;
      this.videoDurationMs = video.duration * 1000;

      this.metadataLoaded.emit({
        durationMs: this.videoDurationMs
      });
    }
  }

  onTimeSelected() {
    if (this.startTime === undefined) {
      this.onCloseDialog();
      return;
    }

    this.videoStartMs = this.startTime.getTime();
    this.videoStartSelected.emit(this.videoStartMs);

    this.showDialog = false;
  }

  // --- SINCRONIZZAZIONE VIDEO ---
  private syncVideo(state: VideoState, video: HTMLVideoElement) {
    if (!this.videoUrl) return;

    const videoTimeMs = state.timeMs - this.videoStartMs;

    // Fuori range → pausa e reset
    if (videoTimeMs < 0 || videoTimeMs > this.videoDurationMs) {
      video.pause();
      video.currentTime = 0;
      return;
    }

    // SEEK preciso
    const targetSeconds = videoTimeMs / 1000;
    if (Math.abs(video.currentTime - targetSeconds) > 0.03) {
      video.currentTime = targetSeconds;
    }

    // PLAY / PAUSE
    if (state.playing && video.paused) video.play();
    if (!state.playing && !video.paused) video.pause();

    // SPEED
    if (video.playbackRate !== state.speed) {
      video.playbackRate = state.speed;
    }
  }
}
